import { useMemo } from 'react';
import * as stylex from '@stylexjs/stylex';
import { Slider, type SliderProps } from '@figma/fpl-components';
import cssVars from '@figma/fpl-tokens/css-vars';
import { spacer } from '@figma/fpl-tokens/tokens.stylex';
import { FormattedNumberInput } from '@/UI/NumberInput/FormattedNumberInput';
import { NumberInputFormatter } from '@/UI/NumberInput/numberInputFormatter';

/** FPL leaves the thumb hollow away from the slider's default value; the design always fills it. */
export const SLIDER_THUMB_STYLE: SliderProps['style'] = { '--fpl-slider-thumb-bg': cssVars.color.iconOnbrand };

/** A slider and a number field side by side, both bounded by `min` and `max`. */
export function SliderField({
  label,
  value,
  min,
  max,
  step,
  bigStep,
  decimals,
  suffix,
  disabled = false,
  onChange,
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  step: number;
  bigStep: number;
  decimals: 0 | 2;
  suffix?: string;
  disabled?: boolean;
  onChange: (value: number) => void;
}) {
  const formatter = useMemo(() => new NumberInputFormatter({ decimals, min, max }), [decimals, min, max]);

  return (
    <div {...stylex.props(styles.row)}>
      <div {...stylex.props(styles.slider)}>
        <Slider
          style={SLIDER_THUMB_STYLE}
          size="md"
          value={value}
          min={min}
          max={max}
          step={step}
          bigStep={bigStep}
          rangeAnchor={min}
          aria-label={label}
          disabled={disabled}
          onChange={onChange}
        />
      </div>
      <FormattedNumberInput
        value={value}
        onSubmit={onChange}
        formatter={formatter}
        ariaLabel={label}
        disabled={disabled}
        suffix={suffix}
        width="64px"
        scrubbable={false}
      />
    </div>
  );
}

const styles = stylex.create({
  row: {
    alignItems: 'center',
    display: 'flex',
    gap: spacer.spacer2,
  },
  slider: {
    flexGrow: 1,
    minWidth: 0,
  },
});
